import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function CompanyConfig() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/company");
        setConfig(r.data);
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, []);

  if (!config) return <div className="p-8 text-slate-500">Cargando…</div>;

  const set = (patch) => setConfig((c) => ({ ...c, ...patch }));
  const setColors = (patch) => set({ colors: { ...config.colors, ...patch } });
  const setNotes = (patch) => set({ default_notes: { ...config.default_notes, ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: config.name, slogan: config.slogan, service_bar: config.service_bar,
        rfc: config.rfc, address: config.address, phone: config.phone,
        email: config.email, website: config.website, whatsapp_number: config.whatsapp_number,
        logo_url: config.logo_url, colors: config.colors, default_notes: config.default_notes,
        iva_rate_default: Number(config.iva_rate_default), isr_rate_default: Number(config.isr_rate_default),
        public_link_default_days: Number(config.public_link_default_days),
        warranty_electrical: config.warranty_electrical,
        warranty_equipment: config.warranty_equipment,
        warranty_labor: config.warranty_labor,
      };
      const r = await api.put("/company", payload);
      setConfig(r.data);
      toast.success("Configuración guardada");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto pb-24" data-testid="company-config-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="pe-label">MÓDULO 6</div>
          <h1 className="text-3xl sm:text-4xl font-black text-[color:var(--pe-primary)]">Configuración de empresa</h1>
          <p className="text-slate-500 mt-1 text-sm">Datos fiscales, WhatsApp, notas predeterminadas por tipo de documento.</p>
        </div>
        <Button onClick={save} disabled={saving} className="pe-btn-primary text-white h-11" data-testid="btn-save-company">
          <Save size={16} className="mr-1" /> {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
      </div>

      <Section title="Identidad de marca">
        <Field label="Nombre comercial">
          <Input value={config.name || ""} onChange={(e) => set({ name: e.target.value })} data-testid="cfg-name" />
        </Field>
        <Field label="Eslogan (aparece en cada documento)">
          <Input value={config.slogan || ""} onChange={(e) => set({ slogan: e.target.value })} data-testid="cfg-slogan" />
        </Field>
        <Field label="Barra de servicios (encabezado documentos)" full>
          <Input value={config.service_bar || ""} onChange={(e) => set({ service_bar: e.target.value })} data-testid="cfg-service-bar" />
        </Field>
        <Field label="URL del logo" full>
          <Input value={config.logo_url || ""} onChange={(e) => set({ logo_url: e.target.value })} />
        </Field>
        <Field label="Color primario"><Input value={config.colors?.primary || ""} onChange={(e) => setColors({ primary: e.target.value })} /></Field>
        <Field label="Color secundario"><Input value={config.colors?.secondary || ""} onChange={(e) => setColors({ secondary: e.target.value })} /></Field>
        <Field label="Color acento (botones)"><Input value={config.colors?.accent || ""} onChange={(e) => setColors({ accent: e.target.value })} /></Field>
      </Section>

      <Section title="Datos fiscales y contacto">
        <Field label="RFC de la empresa"><Input value={config.rfc || ""} onChange={(e) => set({ rfc: e.target.value.toUpperCase() })} data-testid="cfg-rfc" /></Field>
        <Field label="Teléfono principal"><Input value={config.phone || ""} onChange={(e) => set({ phone: e.target.value })} data-testid="cfg-phone" /></Field>
        <Field label="Email"><Input type="email" value={config.email || ""} onChange={(e) => set({ email: e.target.value })} /></Field>
        <Field label="Sitio web"><Input value={config.website || ""} onChange={(e) => set({ website: e.target.value })} /></Field>
        <Field label="Dirección" full><Input value={config.address || ""} onChange={(e) => set({ address: e.target.value })} data-testid="cfg-address" /></Field>
        <Field label="WhatsApp (con LADA, solo dígitos ej: 525555555555)" full>
          <Input value={config.whatsapp_number || ""} onChange={(e) => set({ whatsapp_number: e.target.value.replace(/\D/g, "") })} data-testid="cfg-whatsapp" />
        </Field>
      </Section>

      <Section title="Impuestos por defecto y link público">
        <Field label="IVA por defecto (%)">
          <Input type="number" step="0.01" value={config.iva_rate_default}
                 onChange={(e) => set({ iva_rate_default: e.target.value })} data-testid="cfg-iva-default" />
        </Field>
        <Field label="ISR por defecto (%)">
          <Input type="number" step="0.01" value={config.isr_rate_default}
                 onChange={(e) => set({ isr_rate_default: e.target.value })} />
        </Field>
        <Field label="Vigencia link público (días)">
          <Input type="number" min={1} value={config.public_link_default_days}
                 onChange={(e) => set({ public_link_default_days: e.target.value })} />
        </Field>
      </Section>

      <Section title="Garantías (aparecen en la landing y al final de cada cotización)">
        <p className="text-sm text-slate-500 -mt-2 mb-2 sm:col-span-2">
          Se muestran automáticamente en la sección <b>Políticas de entrega y servicio</b> de la
          landing pública y en cada cotización (PDF + link público). Edita aquí y se reflejan al instante.
        </p>
        <Field label="Instalaciones eléctricas ejecutadas">
          <Input value={config.warranty_electrical || ""}
                 onChange={(e) => set({ warranty_electrical: e.target.value })}
                 data-testid="cfg-warranty-electrical" placeholder="ej: 1 año" />
        </Field>
        <Field label="Equipos suministrados">
          <Input value={config.warranty_equipment || ""}
                 onChange={(e) => set({ warranty_equipment: e.target.value })}
                 data-testid="cfg-warranty-equipment" placeholder="ej: Garantía de fábrica del proveedor" />
        </Field>
        <Field label="Mano de obra" full>
          <Input value={config.warranty_labor || ""}
                 onChange={(e) => set({ warranty_labor: e.target.value })}
                 data-testid="cfg-warranty-labor" placeholder="ej: 3 meses" />
        </Field>
      </Section>

      <Section title="Notas predeterminadas por tipo">
        <p className="text-sm text-slate-500 -mt-2 mb-2 sm:col-span-2">
          Se prellenan automáticamente al crear una cotización según el tipo (producto o servicio).
          Antes esto <b>nunca quedaba guardado</b>, obligaba a reescribirlo a mano cada vez.
        </p>
        <Field label="Nota por defecto para PRODUCTO" full>
          <Textarea rows={5} value={config.default_notes?.producto || ""}
                    onChange={(e) => setNotes({ producto: e.target.value })} data-testid="cfg-note-producto" />
        </Field>
        <Field label="Nota por defecto para SERVICIO" full>
          <Textarea rows={5} value={config.default_notes?.servicio || ""}
                    onChange={(e) => setNotes({ servicio: e.target.value })} data-testid="cfg-note-servicio" />
        </Field>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="pe-card p-6 mt-6">
      <h2 className="text-lg font-black text-[color:var(--pe-primary)] mb-4">{title}</h2>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`space-y-1 ${full ? "sm:col-span-2" : ""}`}>
      <Label className="pe-label">{label}</Label>
      {children}
    </div>
  );
}
